import {
  useEffect, useMemo, useState
} from "react";
import {
  getUserFacingApiErrorMessage
} from "../services/api";
import {
  PairingModalShell
} from "./pairing/PairingModalShell";

/** The shape both personal read grants (Slack GET-14181, Teams GET-14326) share. */
export interface PersonalReadConversation {
  id: string;
  name: string;
  kind: string;
  allowed: boolean;
}

export type PersonalReadAccessMode = "opt_in" | "opt_out";

export interface PersonalReadAccessConfig<C extends PersonalReadConversation> {
  title: string;
  intro: string;
  /** Radio group name, so two modals on one page never share a group. */
  radioName: string;
  searchPlaceholder: string;
  kindOrder: Record<string, number>;
  kindLabel: Record<string, string>;
  /** How a row's name is shown (Slack prefixes channels with `#`). */
  displayName: (c: C) => string;
  incompleteNote: string;
  loadError: string;
  listConversations: () => Promise<{
    conversations: C[];
    access_mode: PersonalReadAccessMode;
    complete?: boolean;
  }>;
  /** `listedIds` is every id the picker rendered, so a provider whose
   *  listing is capped can keep stored ids it never showed. */
  saveAccess: (
    mode: PersonalReadAccessMode,
    ids: string[],
    listedIds: string[],
  ) => Promise<unknown>;
}

interface PersonalReadAccessModalProps<C extends PersonalReadConversation> {
  isOpen: boolean;
  onClose: () => void;
  onDisconnect: () => Promise<void>;
  /** Fired after the list is saved so the tile's detail can refresh. */
  onSaved: () => Promise<void>;
  config: PersonalReadAccessConfig<C>;
}

/**
 * Which of the owner's conversations the assistant may read through their
 * personal (read-only) connection.
 *
 * Two modes, and the checkbox means the same thing in both: ticked = the
 * assistant may read it. Under the hood "Hidden" (opt-in) stores the ticked ids
 * and "Readable" (opt-out) stores the unticked ones, so switching mode keeps
 * the visible selection rather than inverting it (GET-14303).
 */
export function PersonalReadAccessModal<C extends PersonalReadConversation>({
  isOpen,
  onClose,
  onDisconnect,
  onSaved,
  config,
}: PersonalReadAccessModalProps<C>) {
  const [conversations, setConversations] = useState<C[]>([]);
  const [complete, setComplete] = useState(true);
  const [mode, setMode] = useState<PersonalReadAccessMode>("opt_in");
  const [allowed, setAllowed] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A save after a failed load would post the empty default list and wipe
  // the owner's configuration; Save stays disabled until a load succeeds.
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLoadFailed(false);
    config
      .listConversations()
      .then((data) => {
        if (cancelled) return;
        setConversations(data.conversations);
        setComplete(data.complete ?? true);
        setMode(data.access_mode);
        setAllowed(new Set(data.conversations.filter((c) => c.allowed).map((c) => c.id)));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadFailed(true);
        setError(getUserFacingApiErrorMessage(err, config.loadError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const rows = needle
      ? conversations.filter((c) => c.name.toLowerCase().includes(needle))
      : conversations;
    return [...rows].sort(
      (a, b) =>
        (config.kindOrder[a.kind] ?? 9) - (config.kindOrder[b.kind] ?? 9)
        || a.name.localeCompare(b.name),
    );
  }, [conversations, filter, config]);

  const toggle = (id: string) => {
    setAllowed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setAll = (value: boolean) => {
    setAllowed(value ? new Set(conversations.map((c) => c.id)) : new Set());
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const ids = conversations
        .filter((c) => (mode === "opt_in" ? allowed.has(c.id) : !allowed.has(c.id)))
        .map((c) => c.id);
      await config.saveAccess(mode, ids, conversations.map((c) => c.id));
      await onSaved();
      onClose();
    } catch (err) {
      setError(getUserFacingApiErrorMessage(err, "Couldn't save. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    setSaving(true);
    try {
      await onDisconnect();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <PairingModalShell isOpen={isOpen} onClose={onClose} title={config.title}>
      <p className="text-sm text-gray-600 mb-4">{config.intro}</p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <fieldset className="flex items-center gap-2 mb-3 text-sm">
        <legend className="float-left text-gray-700">Conversations not listed here:</legend>
        <label className="inline-flex items-center gap-1.5">
          <input
            type="radio"
            name={config.radioName}
            checked={mode === "opt_in"}
            onChange={() => setMode("opt_in")}
          />
          Hidden
        </label>
        <label className="inline-flex items-center gap-1.5 ml-3">
          <input
            type="radio"
            name={config.radioName}
            checked={mode === "opt_out"}
            onChange={() => setMode("opt_out")}
          />
          Readable
        </label>
      </fieldset>
      <p className="text-xs text-gray-500 mb-3">
        {mode === "opt_in"
          ? "A conversation you join later, or one we couldn't list, stays hidden until you tick it here."
          : "A conversation you join later, or one we couldn't list, is readable unless you untick it here."}
      </p>

      <div className="flex items-center gap-2 mb-2">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={config.searchPlaceholder}
          className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-md"
        />
        <button type="button" onClick={() => setAll(true)} className="text-xs text-indigo-600 hover:underline">
          All
        </button>
        <button type="button" onClick={() => setAll(false)} className="text-xs text-indigo-600 hover:underline">
          None
        </button>
      </div>

      {!complete && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-2">
          {config.incompleteNote}
        </p>
      )}
      <div className="max-h-72 overflow-y-auto border border-gray-100 rounded-md divide-y divide-gray-100 mb-4">
        {loading ? (
          <div className="py-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto" />
          </div>
        ) : visible.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">No conversations found.</p>
        ) : (
          visible.map((c) => (
            <label key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={allowed.has(c.id)} onChange={() => toggle(c.id)} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-gray-900 truncate">{config.displayName(c)}</span>
                <span className="block text-[11px] text-gray-400">{config.kindLabel[c.kind] ?? c.kind}</span>
              </span>
            </label>
          ))
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={disconnect}
          disabled={saving || loading}
          className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
        >
          Disconnect
        </button>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading || loadFailed}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-md"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </PairingModalShell>
  );
}
